import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO, { SEO_DATA } from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Check, ArrowRight, Wrench, ClipboardList, Settings, Award } from 'lucide-react';

export default function OperationsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO {...SEO_DATA.operations} />
      <Header />
      
      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-gradient-hero py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Badge className="bg-white/10 text-white border-white/20 mb-6">Specialized Help</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              Resume Help for Operations & Technician Roles
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
              Operations professionals and technicians need resumes that showcase technical skills, certifications, and process improvements that drive results.
            </p>
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg"
            >
              Start Your Intake <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* Common Problems */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
            Common Resume Problems for Operations Roles
          </h2>
          
          <div className="space-y-4">
            {[
              { problem: 'Listing tasks instead of achievements', fix: 'We quantify improvements—efficiency gains, cost savings, safety records, production metrics.' },
              { problem: 'Certifications buried or missing', fix: 'We prominently feature relevant certifications and technical qualifications.' },
              { problem: 'Technical skills poorly organized', fix: 'We create clear skills sections that highlight tools, equipment, and software proficiency.' },
              { problem: 'Resume doesn\'t show career progression', fix: 'We highlight growth from technician to senior roles or supervisory positions.' },
            ].map((item, i) => (
              <Card key={i} className="p-6 rounded-2xl border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-red-600 font-bold">✕</span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 mb-1">{item.problem}</p>
                    <p className="text-slate-600 text-sm">→ {item.fix}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* What Gets Improved */}
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-12" style={{ fontFamily: 'Outfit' }}>
              What We Improve for Operations Resumes
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Wrench, title: 'Technical Skills', desc: 'Clear presentation of equipment, tools, software, and technical competencies.' },
                { icon: Award, title: 'Certifications', desc: 'Prominent display of industry certifications, licenses, and training credentials.' },
                { icon: ClipboardList, title: 'Process Improvements', desc: 'Quantified achievements showing efficiency gains and cost savings.' },
                { icon: Settings, title: 'Safety & Compliance', desc: 'Highlight safety records, compliance experience, and quality control.' },
                { icon: Check, title: 'Metrics & Numbers', desc: 'Production volumes, uptime percentages, and other relevant KPIs.' },
                { icon: Check, title: 'Career Progression', desc: 'Show advancement from entry-level to senior or supervisory roles.' },
              ].map((item, i) => (
                <Card key={i} className="p-6 rounded-2xl border border-slate-200">
                  <item.icon className="h-8 w-8 text-teal-600 mb-4" />
                  <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Roles We Help With */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
            Operations & Technician Roles We Help With
          </h2>
          
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'Operations Manager',
              'Production Technician',
              'Maintenance Technician',
              'Manufacturing Associate',
              'Quality Control Inspector',
              'Warehouse Operations',
              'Field Service Technician',
              'HVAC Technician',
              'Electrician',
              'Machine Operator',
              'Logistics Coordinator',
              'Facilities Manager',
            ].map((role, i) => (
              <Badge key={i} className="bg-slate-100 text-slate-700 px-4 py-2 text-sm">{role}</Badge>
            ))}
          </div>
        </section>

        {/* Related Pages */}
        <section className="bg-slate-100 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
              Explore More
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/for/customer-service" className="text-teal-700 hover:text-teal-800 underline">Customer Service Jobs</Link>
              <span className="text-slate-300">|</span>
              <Link to="/for/career-change" className="text-teal-700 hover:text-teal-800 underline">Career Changers</Link>
              <span className="text-slate-300">|</span>
              <Link to="/services/resume-rewrite" className="text-teal-700 hover:text-teal-800 underline">Resume Rewrite Service</Link>
              <span className="text-slate-300">|</span>
              <Link to="/pricing" className="text-teal-700 hover:text-teal-800 underline">View Pricing</Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Card className="p-8 rounded-3xl bg-slate-900 text-white">
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              Ready to improve your operations resume?
            </h3>
            <p className="text-slate-300 mb-6">
              Submit your intake and we'll review your background personally. No payment until scope is confirmed.
            </p>
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8"
            >
              Start Your Intake <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
}
